import {Button, Col, Modal, Row, Spin} from 'antd';
import React, {useEffect, useState, useMemo} from 'react';
import styles from './Checkout.module.scss';
import PaymentMethod from '../../components/features/Summary/PaymentMethod';
import Summary from '../../components/features/Summary/Summary';
import {IError} from '../../types/api';
import UserDetail from '../../components/features/UserProfile/UserDetail';
import {Link, useMatch} from 'react-router-dom';
import useHomeStore from "../../store/homeStore";
import useAuthStore from "../../store/authStore";
import useCartStore from "../../store/cartStore";
import useGetShoppingCartQuery from "../../queries/useGetShoppingCartQuery";
import useGetPreCheckoutCheckQuery from "../../queries/useGetPreCheckoutCheckQuery";

export default function CheckoutPage() {
  const isPaymentFailedRoute = useMatch({
    path: '/checkout/paymentfailure',
  });

  const setShoppingItems = useCartStore(state => state.setShoppingItems);

  const isAuthenticated = useAuthStore(state => state.isAuthenticated())
  const setUserDetails = useAuthStore((state) => state.setUserProfile)

  const setShowLogin = useHomeStore(state => state.setShowLogin);
  const setShowUserProfile = useHomeStore(state => state.setShowUserProfile);
  const loadingDataFromBSApi = useHomeStore(state => state.loadingDataFromBSApi);

  const [preCheckOutError, setPreCheckOutError] = useState<IError>({
    ErrorMessage: '',
  });
  const [loadingUserInfo, setLoadingUserInfo] = useState(false);
  const [priceIsZero, setPriceIsZero] = useState(false);

  const shoppingCartWithStatus = useGetShoppingCartQuery(true);
  const preCheckoutCheck = useGetPreCheckoutCheckQuery();

  const loading = useMemo(() => {
    return loadingDataFromBSApi || loadingUserInfo
  }, [loadingDataFromBSApi, loadingUserInfo])

  useEffect(() => {
    setShowLogin(!isAuthenticated);

    if (!isAuthenticated) {
      resetUserInfo();
      setShoppingItems({});
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setLoadingUserInfo(true);
    // We need to verify if user has been created in Maconomy
    const result = preCheckoutCheck.data;

    if (result?.Error && result?.Error.ErrorMessage) {
      setUserDetails(null);
      setPreCheckOutError(result?.Error);
    } else if (result?.Result) {
      const company = result.Result.companyInfo;
      setUserDetails({
        ...result.Result,
        password: '',
        confirm_password: '',
        // company: company && company.name ? company.name : "",
        countryId:
          company && company.countryId ? company.countryId.toString() : '', // "999990000" Denmark
        companyId: company && company.vatNumber ? company.vatNumber : '',
        stateId: company && company.stateId ? company.stateId.toString() : '',
        sizeId: company && company.sizeId ? company.sizeId.toString() : '', // "999992300",//
        segmentId:
          company && company.segmentId ? company.segmentId.toString() : '', // "999992203"//
        city: company && company.city ? company.city : '',
        zipcode: company && company.postalcode ? company.postalcode : '',
        address1: company && company.address1 ? company.address1 : '',
        address2: company && company.address2 ? company.address2 : '',
        address3: company && company.address3 ? company.address3 : '',
      });
    }
    setLoadingUserInfo(false);
  }, [preCheckoutCheck?.data, setLoadingUserInfo]);

  useEffect(() => {
    const data = shoppingCartWithStatus.data;

    if (!data) return

    setPriceIsZero(data.totalPrice === 0);
    setShoppingItems(data.shoppingItems);
  }, [shoppingCartWithStatus?.data, setShoppingItems, setPriceIsZero]);

  function resetUserInfo() {
    setUserDetails({
      companyId: '',
      address: '',
      address1: '',
      address2: '',
      address3: '',
      city: '',
      company: '',
      country: '',
      countryId: '',
      sizeId: '',
      stateId: '',
      segmentId: '',
      firstname: '',
      lastname: '',
      mobile: '',
      phone: '',
      userId: '',
      zipcode: '',
      editProfileUrl: '',
    });
  }

  function onClosePreCheckOutFailedDialog() {
    setShowUserProfile(true);
    setPreCheckOutError({ErrorMessage: ''});
  }

  return (
    <div className={styles.checkoutRoot}>
      <Modal
        closable={false}
        keyboard={false}
        open={preCheckOutError.ErrorMessage !== ''}
        title={
          preCheckOutError.Code === 1
            ? 'Your user profile is not complete'
            : 'Error during checkout preprocessing'
        }
        footer={
          <Link to={'/app'}>
            <Button onClick={onClosePreCheckOutFailedDialog}>OK</Button>
          </Link>
        }
      >
        <p>
          {preCheckOutError.Code === 1
            ? preCheckOutError.ErrorMessage.replace(
              'companyId',
              'Company VAT number'
            )
            : preCheckOutError.ErrorMessage}
        </p>
      </Modal>

      <Modal
        closable={false}
        keyboard={false}
        open={isPaymentFailedRoute !== null}
        title={'Payment failed'}
        footer={
          <Link to={'/checkout'}>
            <Button>OK</Button>
          </Link>
        }
      >
        <div>
          {'Please try again or '}
          <Link to={'/contact'}> {'contact us.'}</Link>
        </div>
      </Modal>

      <div className={styles.checkoutHead}>
        <div className={styles.checkoutContainer}>
          <h2 className={styles.checkoutTitle}>Checkout</h2>
        </div>
      </div>

      <div className={styles.checkoutContainer}>
        <Row
          gutter={{xs: 8, sm: 16, md: 24, lg: 32}}
          style={{width: '100%'}}
        >
          <Col xs={{span: 24}} md={{span: 16}}>
            {/*  // ! Personal details */}
            <div className={styles.itemTitle}>
              <span className={styles.itemTitleOrder}>1.</span>
              <span>Personal and Company details</span>
            </div>
            {loading ? (
              <div className={styles.spinner}>
                <Spin size="large"/>
              </div>
            ) : (
              <UserDetail
                subscription={{type: '', expiredDate: ''}}
                downloaderApiKey={''}
                readOnly={true}
              />
            )}

            {/* // ! Delivery Method */}
            <div className={styles.itemTitle}>
              <span className={styles.itemTitleOrder}>2.</span>
              <span>Delivery Method</span>
            </div>
            <div>
              {
                "By clicking 'CHECKOUT', if the total price is above 0 EUR, you will be directed to the payment gateway. If the total price is 0 EUR, then you will be directed back to the main page. Within a few minutes after completing the checkout, you will receive an email with a link to download the data in a .zip file. The download link is valid for 30 days. In the shopping cart you can view a sample of the data file(s) content in .csv format. Validations, Analytics and Reports are not provided by email."
              }
            </div>

            {/*  <div className={styles.itemRow}>
              {deliveryMethod.length === 1 ?  (
                <div key={deliveryMethod[0].id}>
                  {deliveryMethod[0].delivery_desc}
                </div>) :
               (deliveryMethod.map((DM) => {
                return (
                  <div key={DM.id} className={styles.deliveryMethod}>
                    <Checkbox
                      checked={DM.checked}
                      onChange={(e) => changeDeliveryMethod(DM, e.target.checked)}
                    >
                      {DM.delivery_desc}
                    </Checkbox>
                    <span className={styles.deliveryMethodPrice}>+€{DM.price}</span>
                  </div>
                );
              }))}
            </div> */}

            {!priceIsZero && (
              <>
                <div className={styles.itemTitle}>
                  <span className={styles.itemTitleOrder}>3.</span>
                  <span>Payment Method</span>
                </div>
                <PaymentMethod/>
              </>
            )}
          </Col>
          <Col xs={{span: 24}} md={{span: 8}}>
            <div className={styles.itemTitle}>Summary</div>
            {loading ? (
              <div className={styles.spinner}>
                <Spin size="large"/>
              </div>
            ) : (
              <Summary/>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
}
